import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { callOffAPI } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';

interface StockCorrectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partNumber: string;
  description?: string;
  currentQty: number | string;
  unitOfMeasure?: string;
  customerId?: number;
  onCorrected: () => void;
}

const MIN_REASON_LEN = 3;

/**
 * Modal for absolute stock corrections. Replaces warehouse_stock with the
 * entered qty and forces the user to type a non-trivial reason — the
 * reason lands in StockChangeLog.comments so P&A can trace why a
 * paid-for unit later disappeared from running totals.
 */
export default function StockCorrectionDialog({
  open,
  onOpenChange,
  partNumber,
  description,
  currentQty,
  unitOfMeasure,
  customerId,
  onCorrected,
}: StockCorrectionDialogProps) {
  const [newQty, setNewQty] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setNewQty(String(currentQty ?? ''));
      setReason('');
    }
  }, [open, currentQty]);

  const trimmedReason = reason.trim();
  const parsedQty = parseFloat(newQty);
  const qtyValid = newQty.trim() !== '' && !isNaN(parsedQty) && parsedQty >= 0;
  const reasonValid = trimmedReason.length >= MIN_REASON_LEN;
  const qtyChanged = qtyValid && parsedQty !== Number(currentQty);
  const canSubmit = qtyValid && reasonValid && qtyChanged && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await callOffAPI.correctStock({
        item_code: partNumber,
        quantity: parsedQty,
        reason: trimmedReason,
        ...(customerId ? { customer_id: customerId } : {}),
      });
      toast.success(`Stock for ${partNumber} corrected`);
      onCorrected();
      onOpenChange(false);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Correction failed'), { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Correct stock — {partNumber}</DialogTitle>
          <DialogDescription>
            Replace the running warehouse total with a corrected value. A reason is required so the
            change is traceable in the audit log.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {description && (
            <p className="text-xs text-muted-foreground truncate" title={description}>
              {description}
            </p>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Current total</Label>
              <p className="text-sm font-mono mt-1">{currentQty} {unitOfMeasure || ''}</p>
            </div>
            <div>
              <Label htmlFor="corrected-qty" className="text-xs">New total <span className="text-red-500">*</span></Label>
              <Input
                id="corrected-qty"
                type="number"
                min={0}
                step="0.001"
                value={newQty}
                onChange={(e) => setNewQty(e.target.value)}
                className="mt-1"
                autoFocus
              />
            </div>
          </div>

          <div>
            <Label htmlFor="correction-reason" className="text-xs">
              Reason for change <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="correction-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Recount: actual is 30 units, not 50. Mis-keyed yesterday."
              rows={3}
              className="mt-1"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {trimmedReason.length}/500 characters
              {trimmedReason.length > 0 && trimmedReason.length < MIN_REASON_LEN && (
                <span className="text-amber-600 ml-2">(min {MIN_REASON_LEN} characters)</span>
              )}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save correction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
